/**
 * Casual Competitors - 头部厂商下载 Excel 配置
 * @description 配置头部厂商模块的 Excel 列头和数据映射
 * @note 下载结果需与页面外层公司表和内嵌游戏表的当前顺序保持一致
 */
import { isEmpty } from '@intelligence/v3/utils/common';
import { formatDateStr } from '@intelligence/v3/utils/date';
import { formatDecimalToPercentage } from '@intelligence/v3/utils/number';
import { buildDownloadDataNew } from '@intelligence/utils/commonUtils';
import { DataCalculation } from '@intelligence/v3/enums/download';
import { DOWNLOAD_CFG_KEY } from '@intelligence/v3/constants';
import { sortDownloadDataByConfig } from '@intelligence/components/DownloadCommon/DownloadPage/utils';

const formatList = <T extends IRecord>(list?: T[] | null): T[] => {
  if (isEmpty(list)) {
    return [];
  }
  return list!;
};

const formatMetricCellValue = (value: unknown): string | number => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : '';
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (trimmedValue === '') {
      return '';
    }

    return Number.isFinite(Number(trimmedValue)) ? value : '';
  }

  return '';
};

const getCompanyGameSortKey = (companyId?: string, gameId?: string): string => {
  return `${companyId || ''}__${gameId || ''}`;
};

export const getCasualCompetitorsTopPublishersConfig = (data: any, params: any, allParams: any) => {
  const granularity = params?.granularity || 'monthly';
  const isGroupByDate = allParams?.[DOWNLOAD_CFG_KEY.DATE_METHOD] !== DataCalculation.Aggregated;
  const isGroupByRegion = allParams?.[DOWNLOAD_CFG_KEY.REGION_METHOD] === DataCalculation.SeparateByRegion;
  const isGroupByMarket = allParams?.[DOWNLOAD_CFG_KEY.REGION_METHOD] === DataCalculation.SeparateByCountry;
  // 是否展示%
  const isShowRatio = allParams?.ratio_show === 1;
  // 是否显示收入指标（从 params 中获取，因为 hasRevenue 在 downloadApiParams 中传递）
  const hasRevenue = params?.hasRevenue !== false;

  // 从 Companies API 响应中获取厂商信息
  // data 结构: { Companies: { list, $ext/ext }, Games: { list, $ext/ext } }
  // 注意：getDownloadData 返回的数据中，ext 信息在 $ext 字段
  // SSE 模式返回的数据中，ext 信息在 ext 字段
  const companiesRes = data?.Companies || {};
  const gamesRes = data?.Games || {};

  // 厂商列表数据（来自 Companies API）
  const companiesList = formatList(companiesRes?.list);
  // 厂商实体信息（兼容 $ext 和 ext 两种格式）
  const companiesExtInfo = companiesRes?.$ext || companiesRes?.ext || {};
  const companiesEntityInfo = companiesExtInfo?.entity_info || {};

  // 使用 Games 响应的 ext 信息（包含游戏的 entity_info 和 market_info）
  // 兼容 $ext 和 ext 两种格式
  const gamesExtInfo = gamesRes?.$ext || gamesRes?.ext || {};
  const entityInfo = gamesExtInfo?.entity_info || {};
  const marketInfo = gamesExtInfo?.market_info || {};
  const regionNameENMap = marketInfo?.['intel_region -> en'] || {};
  const regionNameCNMap = marketInfo?.['intel_region -> cn'] || {};
  const marketNameENMap = marketInfo?.['market -> en'] || {};
  const marketNameCNMap = marketInfo?.['market -> cn'] || {};
  const regionCodeMap = marketInfo?.['market -> region'] || {};
  const pageCompanyIds = Array.isArray(allParams?.page_company_ids) ? allParams?.page_company_ids : [];
  const pageCompanyGameIdsMap =
    allParams?.page_company_game_ids_map && typeof allParams?.page_company_game_ids_map === 'object'
      ? allParams?.page_company_game_ids_map
      : {};
  const pageCompanyGameSortKeys = pageCompanyIds.flatMap((companyId: string) => {
    const companyGameIds = Array.isArray(pageCompanyGameIdsMap?.[companyId]) ? pageCompanyGameIdsMap?.[companyId] : [];
    return companyGameIds.map((gameId: string) => getCompanyGameSortKey(companyId, gameId));
  });

  const regionCountryConfig = [
    {
      title: 'Region_English',
      key: (item: any) => {
        if (isGroupByRegion) {
          const itemRegionCode = (item?.region || '').toLowerCase();
          return regionNameENMap?.[itemRegionCode] || itemRegionCode;
        }
        const itemMarketCode = item?.market || '';
        const itemRegionCode = regionCodeMap?.[itemMarketCode]?.toLowerCase() || '';
        return regionNameENMap?.[itemRegionCode] || itemRegionCode;
      },
      when: () => isGroupByRegion || isGroupByMarket,
    },
    {
      title: 'Region_Chinese',
      key: (item: any) => {
        if (isGroupByRegion) {
          const itemRegionCode = (item?.region || '').toLowerCase();
          return regionNameCNMap?.[itemRegionCode] || itemRegionCode;
        }
        const itemMarketCode = item?.market || '';
        const itemRegionCode = regionCodeMap?.[itemMarketCode]?.toLowerCase() || '';
        return regionNameCNMap?.[itemRegionCode] || itemRegionCode;
      },
      when: () => isGroupByRegion || isGroupByMarket,
    },
    {
      title: 'Country_English',
      key: (item: any) => {
        const itemMarketCode = (item?.market || '').toLowerCase();
        return marketNameENMap?.[itemMarketCode] || itemMarketCode;
      },
      when: () => isGroupByMarket,
    },
    {
      title: 'Country_Chinese',
      key: (item: any) => {
        const itemMarketCode = (item?.market || '').toLowerCase();
        return marketNameCNMap?.[itemMarketCode] || itemMarketCode;
      },
      when: () => isGroupByMarket,
    },
  ];

  // ============================================
  // Companies 标签页配置（来自 Companies API 返回的数据）
  // ============================================
  // 处理厂商数据，添加厂商名称和国家信息
  const companiesData = companiesList.map((item: any) => {
    const companyId = item?.company_id || '';
    const companyEntity = companiesEntityInfo?.[companyId] || {};
    return {
      ...item,
      // 从 entity_info 获取厂商名称和国家
      company_name: companyEntity?.name || '',
      company_country: companyEntity?.country_name_en || companyEntity?.country_abbre || '',
      // 游戏数量来自 API 返回的 company_game_count 字段
      game_count: item?.company_game_count || 0,
    };
  });
  const companiesDataMap = companiesData.reduce<Record<string, IRecord>>((acc, item: any) => {
    const companyId = item?.company_id || '';
    if (companyId) {
      acc[companyId] = item;
    }
    return acc;
  }, {});

  const companiesSheetConfig = [
    {
      title: 'Company Name',
      key: (item: any) => item?.company_name || '',
    },
    {
      title: 'Game owned',
      key: (item: any) => item?.game_count || 0,
    },
    {
      title: 'Date',
      key: (item: any) => {
        const itemDate = item?.date || '';
        return granularity === 'monthly' ? formatDateStr(itemDate, 'YYYY-MM') : itemDate;
      },
      when: () => isGroupByDate,
    },
    ...regionCountryConfig,
    {
      title: `${granularity === 'monthly' ? 'Monthly' : 'Daily'} Avg.DAU`,
      key: (item: any) => formatMetricCellValue(item?.sensortower__daily_avg_dau),
    },
    {
      title: '%',
      key: (item: any) => formatMetricCellValue(item?.sensortower__daily_avg_dau_ratio),
      when: () => isShowRatio,
    },
    {
      title: `Cumulative ${granularity === 'monthly' ? 'Monthly' : 'Daily'} IAP Revenue($)`,
      key: (item: any) => formatMetricCellValue(item?.sensortower__iap_revenue),
      when: () => hasRevenue,
    },
    {
      title: '%',
      key: (item: any) => formatMetricCellValue(item?.sensortower__iap_revenue_ratio),
      when: () => isShowRatio && hasRevenue,
    },
    {
      title: `Cumulative ${granularity === 'monthly' ? 'Monthly' : 'Daily'} Downloads`,
      key: (item: any) => formatMetricCellValue(item?.sensortower__download),
    },
    {
      title: '%',
      key: (item: any) => formatMetricCellValue(item?.sensortower__download_ratio),
      when: () => isShowRatio,
    },
    {
      title: 'Company Country',
      key: (item: any) => item?.company_country || '',
    },
  ];

  // ============================================
  // Games 标签页配置
  // ============================================
  // Games 标签页使用的数据（来自 Games API 响应）
  const gamesList = formatList(gamesRes?.list);
  const gamesData = gamesList.map((item: any) => {
    const companyId = item?.company_id || '';
    const companyInfo = companiesDataMap?.[companyId] || {};

    return {
      ...item,
      company_name: companyInfo?.company_name || '',
      company_country: companyInfo?.company_country || '',
    };
  });

  const gamesSheetConfig = [
    {
      title: 'Company Name',
      key: (item: any) => item?.company_name || '',
    },
    {
      title: 'Game Name',
      key: (item: any) => {
        const gameId = item?.game_id || '';
        return entityInfo?.[gameId]?.entity_name || gameId;
      },
    },
    {
      title: 'Date',
      key: (item: any) => {
        const itemDate = item?.date || '';
        return granularity === 'monthly' ? formatDateStr(itemDate, 'YYYY-MM') : itemDate;
      },
      when: () => isGroupByDate,
    },
    ...regionCountryConfig,
    {
      title: `${granularity === 'monthly' ? 'Monthly' : 'Daily'} Avg.DAU`,
      key: (item: any) => formatMetricCellValue(item?.sensortower__daily_avg_dau),
    },
    {
      title: '%',
      key: (item: any) => formatMetricCellValue(item?.sensortower__daily_avg_dau_ratio),
      when: () => isShowRatio,
    },
    {
      title: `${granularity === 'monthly' ? 'Monthly' : 'Daily'} IAP Revenue($)`,
      key: (item: any) => formatMetricCellValue(item?.sensortower__iap_revenue),
      when: () => hasRevenue,
    },
    {
      title: '%',
      key: (item: any) => formatMetricCellValue(item?.sensortower__iap_revenue_ratio),
      when: () => isShowRatio && hasRevenue,
    },
    {
      title: `${granularity === 'monthly' ? 'Monthly' : 'Daily'} Downloads`,
      key: (item: any) => formatMetricCellValue(item?.sensortower__download),
    },
    {
      title: '%',
      key: (item: any) => formatMetricCellValue(item?.sensortower__download_ratio),
      when: () => isShowRatio,
    },
    {
      title: 'Active User Share',
      key: (item: any) => formatDecimalToPercentage(item?.sensortower__dau_share),
    },
    {
      title: 'Genre',
      key: (item: any) => {
        const gameId = item?.game_id || '';
        return entityInfo?.[gameId]?.sensortower_sub_genre || '';
      },
    },
    {
      title: 'Company',
      key: (item: any) => item?.company_name || '',
    },
    {
      title: 'Company Country',
      key: (item: any) => item?.company_country || '',
    },
  ];

  // 排序数据：公司顺序 → 游戏顺序 → 日期降序 → 地区排序 → 国家排序
  const regionMethod = allParams?.[DOWNLOAD_CFG_KEY.REGION_METHOD];

  // Companies 排序：按页面外层表格中的公司顺序排序
  const companiesListForSort = companiesData.map((item: any) => ({
    ...item,
    _region: item?.region || regionCodeMap?.[item?.market]?.toLowerCase() || '',
  }));

  const sortedCompaniesList = sortDownloadDataByConfig(companiesListForSort, {
    entityKey: 'company_id',
    entityIds: pageCompanyIds,
    isGroupByDate,
    regionMethod,
    regionKey: '_region',
    marketKey: 'market',
  });

  // 预处理数据：给每条数据添加 _region 字段和公司-游戏排序键
  const listForSort = gamesData.map((item: any) => ({
    ...item,
    _region: item?.region || regionCodeMap?.[item?.market]?.toLowerCase() || '',
    _company_game_sort_key: getCompanyGameSortKey(item?.company_id, item?.game_id),
  }));

  const sortedGamesList = sortDownloadDataByConfig(listForSort, {
    entityKey: '_company_game_sort_key',
    entityIds: pageCompanyGameSortKeys,
    isGroupByDate,
    regionMethod,
    regionKey: '_region',
    marketKey: 'market',
  });

  return {
    companies: {
      sheetName: 'Companies',
      style: 'normal',
      data: buildDownloadDataNew(companiesSheetConfig, sortedCompaniesList),
    },
    games: {
      sheetName: 'Games',
      style: 'normal',
      data: buildDownloadDataNew(gamesSheetConfig, sortedGamesList),
    },
  };
};
